import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { authorizedFetch } from '@nimbitlabs/ui';

export interface Account {
  id: string;
  accountId?: string;
  name: string;
  companyName: string;
  company_name?: string;
  slug: string;
  email: string | null;
  phone: string | null;
  status: string;
  role: string;
  referralCode: string;
  referredByCode: string | null;
  referralCount: number;
  usersCount: number;
  subscriptionsCount: number;
  onboardingCompleted?: boolean;
}

export interface CreateAccountDto {
  name: string;
  companyName: string;
  email: string;
  phone?: string;
  referredByCode?: string;
}

export interface UpdateAccountDto {
  name?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  slug?: string;
}

export interface UpdateReferralCodeDto {
  referralCode: string;
}

interface AccountState {
  account: Account | null;
  accounts: Account[];
  currentAccount: Account | null;
  loading: boolean;
  error: string | null;
  
  fetchAccount: () => Promise<void>;
  fetchAccounts: (options?: { silent?: boolean }) => Promise<void>;
  createAccount: (data: CreateAccountDto) => Promise<Account>;
  updateAccount: (data: UpdateAccountDto) => Promise<Account>;
  updateReferralCode: (data: UpdateReferralCodeDto) => Promise<Account>;
  setAccounts: (accounts: Account[]) => void;
  setCurrentAccount: (account: Account | null) => void;
  setLoading: (loading: boolean) => void;
  clearError: () => void;
  reset: () => void;
}

/**
 * AccountStore - Root store for account management
 * 
 * Dependencies: Auth context (user must be logged in)
 * Dependents: All account-scoped stores subscribe to currentAccount
 * 
 * This is the source of truth for:
 * - Available accounts for the logged-in user
 * - Currently selected account
 * 
 * Flow:
 * 1. User logs in → fetchAccounts() loads all accounts for user
 * 2. User selects account → setCurrentAccount() triggers dependent stores
 * 3. User logs out → reset() clears all data
 */
export const useAccountStore = create<AccountState>()(
  subscribeWithSelector((set, get) => ({
    account: null,
    accounts: [],
    currentAccount: null,
    loading: false,
    error: null,
    
    fetchAccount: async () => {
      set({ loading: true, error: null });
      try {
        const response = await authorizedFetch('/accounts/me');
        if (!response.ok) throw new Error('Failed to fetch account');
        const account = await response.json() as Account;
        set({ account, loading: false });
      } catch (e: any) {
        set({ error: e?.message ?? 'Failed to fetch account', loading: false });
      }
    },
    
    fetchAccounts: async (options?: { silent?: boolean }) => {
      set({ loading: true, error: null });
      try {
        const url = options?.silent ? '/accounts?silent=true' : '/accounts';
        const response = await authorizedFetch(url);
        if (!response.ok) throw new Error('Failed to fetch accounts');
        const data = await response.json();
        const accounts = data.accounts || data || [];
        set({ accounts, loading: false });
      } catch (e: any) {
        set({ error: e?.message ?? 'Failed to fetch accounts', loading: false });
      }
    },
    
    createAccount: async (data: CreateAccountDto) => {
      try {
        const response = await authorizedFetch('/accounts', {
          method: 'POST',
          body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to create account');
        const newAccount = await response.json() as Account;
        set({ accounts: [...get().accounts, newAccount] });
        return newAccount;
      } catch (e: any) {
        set({ error: e?.message ?? 'Failed to create account' });
        throw e;
      }
    },
    
    updateAccount: async (data: UpdateAccountDto) => {
      try {
        const response = await authorizedFetch('/accounts/me', {
          method: 'PATCH',
          body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to update account');
        const updatedAccount = await response.json() as Account;
        set({ account: updatedAccount });
        await get().fetchAccounts();
        return updatedAccount;
      } catch (e: any) {
        set({ error: e?.message ?? 'Failed to update account' });
        throw e;
      }
    },
    
    updateReferralCode: async (data: UpdateReferralCodeDto) => {
      try {
        const response = await authorizedFetch('/accounts/referral-code', {
          method: 'PATCH',
          body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to update referral code');
        const updatedAccount = await response.json() as Account;
        set({ account: updatedAccount });
        return updatedAccount;
      } catch (e: any) {
        set({ error: e?.message ?? 'Failed to update referral code' });
        throw e;
      }
    },
    
    setAccounts: (accounts) => set({ accounts }),
    setCurrentAccount: (account) => set({ currentAccount: account }),
    setLoading: (loading) => set({ loading }),
    clearError: () => set({ error: null }),
    reset: () => set({ account: null, accounts: [], currentAccount: null, loading: false, error: null }),
  }))
);
